// @ts-nocheck
import axios, {AxiosRequestConfig} from 'axios';
import {
  useActionStore,
  useConfigurationStore,
  useNotificationsStore,
  useUserProfileStore,
  HitBannerNotificationItem,
} from '../../store/pinia';
import {globals} from '../../../../app/src/globals';
import {translate} from '../../plugins/i18n/i18n';

interface PostOptions {
  params?: {[key: string]: any};
  values?: Record<string, string>[] | Record<string, string>;
  config?: AxiosRequestConfig;
  customErrorMessage?: boolean;
  downloadAsPdf?: boolean;
}

const defaultPostOptions: PostOptions = {
  params: {},
  values: {},
  config: {},
  customErrorMessage: false,
  downloadAsPdf: false,
};

export class ActionService {
  protected static get baseUrl() {
    return process.env.VUE_APP_ACTION_URL;
  }

  static downloadAsPdf(response) {
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    let filename = '';
    const contentDisposition = response.headers['content-disposition']; // ex: "attachment;filename='FormDefinition complete example 21/08/2020.pdf'"
    if (contentDisposition) {
      filename = (
        (contentDisposition.split('filename=')[1] || '').split(';')[0] || ''
      )
        .replace(/'/g, '')
        .replace(/"/g, '');
    }
    if (!filename) {
      filename = 'form.pdf';
    }
    link.download = filename;
    link.click();
  }

  static get(
    route: string,
    config = {} as AxiosRequestConfig,
    downloadAsPdf = false,
    customErrorMessage = false,
  ) {
    const path = this.baseUrl + route;
    if (!config.headers) {
      config.headers = {};
    }
    if (downloadAsPdf) {
      config['responseType'] = 'arraybuffer';
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    config.headers['Accept-Profile'] = globals.$keycloak.tokenParsed.schema_id;
    try {
      config.headers['Accept-Language'] = [
        useConfigurationStore().userLanguageSnakeCase,
        useConfigurationStore().mainLanguageSnakeCase,
      ].join(',');
    } catch {
      /** During the init of the config store, the get method is already used but
      at this moment, the languages are not available -> throws an error **/
    }
    return axios
      .get(path, config)
      .then((response) => {
        if (downloadAsPdf) {
          this.downloadAsPdf(response);
        } else {
          return response;
        }
      })
      .catch((error) => {
        if (!customErrorMessage) {
          if (error.response) {
            if (error.response.status === 444) {
              // The keys returned by the API need to be defined in the hit-base language dictionary
              useNotificationsStore().insertBannerNotification(
                translate(error.response.data),
              );
              return;
            } else if (error.response.status === 500) {
              useNotificationsStore().insertBannerNotification(
                translate('hit-base.common.error-500'),
              );
              return;
            }
          }
        }
        throw error;
      });
  }

  static post(route: string, options: Partial<PostOptions>) {
    const mergedOptions = {...defaultPostOptions, ...options};
    const {params, values, config, customErrorMessage, downloadAsPdf} =
      mergedOptions;
    const path = this.baseUrl + route;
    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    config.headers['Accept-Profile'] = globals.$keycloak.tokenParsed.schema_id;
    try {
      config.headers['Accept-Language'] = [
        useConfigurationStore().userLanguageSnakeCase,
        useConfigurationStore().mainLanguageSnakeCase,
      ].join(',');
    } catch {
      /** During the init of the config store, languages may not be available yet **/
    }
    if (downloadAsPdf) {
      config['responseType'] = 'arraybuffer';
    }
    config.params = params;
    return axios
      .post(path, values, config)
      .then((response) => {
        if (downloadAsPdf) {
          this.downloadAsPdf(response);
        } else {
          if (response.status === 244) {
            const banner =
              typeof response.data === 'string'
                ? translate(response.data)
                : translate(response.data.message, response.data.payload);
            useNotificationsStore().insertBannerNotification(
              banner,
              HitBannerNotificationItem.SUCCESS,
            );
          } else {
            return response;
          }
        }
      })
      .catch((error) => {
        if (!customErrorMessage) {
          if (error.response) {
            if (
              error.response.status === 344 ||
              error.response.status === 444 ||
              error.response.status === 445
            ) {
              let bannerData = error.response.data;
              if (bannerData instanceof ArrayBuffer) {
                const decoder = new TextDecoder('utf-8');
                bannerData = decoder.decode(bannerData);
                try {
                  bannerData = JSON.parse(bannerData);
                } catch {
                  /*eslint no-empty: "error"*/
                }
              }
              const banner =
                typeof bannerData === 'string'
                  ? translate(bannerData)
                  : translate(bannerData.message, bannerData.payload);
              if (error.response.status === 344) {
                useNotificationsStore().insertBannerNotification(
                  banner,
                  HitBannerNotificationItem.INFO,
                );
              } else {
                useNotificationsStore().insertBannerNotification(banner);
              }
              if (error.response.status === 445) {
                useActionStore().updateAssignedActions();
              }
              return 'Error';
            }
          }
          useNotificationsStore().insertBannerNotification(
            translate('hit-base.banners.unknown-error-title'),
          );
          return;
        }
        throw error;
      });
  }

  static delete(route: string, options: Partial<PostOptions>) {
    const mergedOptions = {...defaultPostOptions, ...options};
    const {params, values, config, customErrorMessage} = mergedOptions;
    const path = this.baseUrl + route;

    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    config.headers['Accept-Profile'] = globals.$keycloak.tokenParsed.schema_id;
    config.headers['Accept-Language'] = [
      useConfigurationStore().userLanguageSnakeCase,
      useConfigurationStore().mainLanguageSnakeCase,
    ].join(',');

    config.params = params;

    return axios
      .delete(path, {
        ...config,
        data: values,
      })
      .then((response) => {
        if (response.status === 244) {
          const banner =
            typeof response.data === 'string'
              ? translate(response.data)
              : translate(response.data.message, response.data.payload);
          useNotificationsStore().insertBannerNotification(
            banner,
            HitBannerNotificationItem.SUCCESS,
          );
        } else {
          return response;
        }
      })
      .catch((error) => {
        if (!customErrorMessage) {
          if (error.response) {
            if (
              error.response.status === 444 ||
              error.response.status === 445
            ) {
              const banner =
                typeof error.response.data === 'string'
                  ? translate(error.response.data)
                  : translate(
                      error.response.data.message,
                      error.response.data.payload,
                    );
              useNotificationsStore().insertBannerNotification(banner);
              if (error.response.status === 445) {
                useActionStore().updateAssignedActions();
              }
              return 'Error';
            }
          }
          useNotificationsStore().insertBannerNotification(
            translate('hit-base.banners.unknown-error-title'),
          );
          return;
        }
        throw error;
      });
  }

  static depositTask(taskId, taskData, silent = false) {
    return ActionService.post('slow-tasks/deposit-task', {
      values: {
        staff_id: useUserProfileStore().keycloakId,
        task_id: taskId,
        task_data: taskData,
        silent: silent,
      },
    }).then((res) => {
      if (!silent) {
        useNotificationsStore().insertNotification(res.data);
      }
    });
  }
}
